'use client';

import React from 'react';
import Link from 'next/link';
import { ProtectedAppImage } from '@/components/ProtectedImage/ProtectedAppImage';
import Header from '@/components/common/Header';
import Footer from '@/app/homepage/components/SiteFooter';
import { useAuth } from '@/contexts/AuthContext';
import { getAuthRedirectUrl } from '@/lib/redirectUtils';

const Check = ({ className = 'w-5 h-5' }: { className?: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
  </svg>
);

export default function ImageEnhancementService() {
  const { isAuthenticated } = useAuth();
  const signupUrl = getAuthRedirectUrl(isAuthenticated);

  const enhancements = [
    { title: 'White Balancing', description: 'Ensures objects that appear white in person are rendered accurately in photos, eliminating yellow or off-white discoloration for bright, crisp imagery.', icon: '⚖️' },
    { title: 'Image Sharpening', description: 'Emphasizes texture and enhances overall photo quality, giving your images a professional, polished finish.', icon: '✨' },
    { title: 'Vertical & Horizontal Straightening', description: 'Corrects wonky horizons and distorted vertical lines to create perfectly composed, eye-pleasing photographs.', icon: '📐' },
    { title: 'Remove Minor Blemishes', description: 'Eliminates spots, dots, marks, and fingerprints to deliver flawless, professional-grade images.', icon: '🧹' },
    { title: 'Lens Distortion Removal', description: 'Fixes curved lines caused by wide-angle lenses, ensuring walls appear straight and natural.', icon: '🔍' },
    { title: 'Brightness & Contrast Adjustment', description: 'Creates depth and visual engagement by optimizing brightness levels and contrast ratios.', icon: '💡' },
    { title: 'Sky Replacement', description: 'Transforms overcast, washed-out, or overexposed skies into stunning clear blue backdrops.', icon: '☁️' },
    { title: 'Lawn Enhancement, Repair or Replace', description: "Revitalizes brown or patchy lawns to showcase the property's full curb appeal potential.", icon: '🌱' },
    { title: 'Ocean/River Water Enhancement', description: 'Enhances water features to appear more vibrant and inviting in your property photos.', icon: '🌊' },
    { title: 'Remove Pool Cleaners from Water', description: 'Eliminates unsightly pool equipment, helping buyers envision themselves relaxing poolside.', icon: '🏊' },
    { title: 'Tone Adjustment', description: 'Applies the perfect tonal balance to create bright, welcoming, and inviting imagery.', icon: '🎨' },
    { title: 'TV Screen Replacement', description: 'Replaces blank TV screens with beautiful imagery while hiding unwanted reflections.', icon: '📺' },
    { title: 'Dust Spot Removal', description: 'Removes lens dust spots and marks that can blur or diminish photo quality.', icon: '✨' },
    { title: 'Flash Reflection Removal', description: 'Corrects dramatic reflections and shadows caused by flash photography for professional results.', icon: '⚡' },
    { title: 'Add Fire to Fireplaces', description: 'Creates a cozy, inviting atmosphere by adding warm, realistic flames to fireplaces.', icon: '🔥' },
    { title: 'HDR Bracketing with Window Replacement', description: 'Balances bright exteriors and dark interiors so images appear natural, just as your eye sees them.', icon: '🌟' },
    { title: "Remove Photographer's Reflection", description: 'Eliminates photographer and camera reflections for clean, streamlined shots.', icon: '👤' },
  ];

  const optionalExtra = {
    title: 'Turn On Lights',
    price: '$0.20',
    description: 'Blown bulbs, electricity off, or forgot to flip the switch? Our editing experts can illuminate every room for you.',
    icon: '💡',
  };

  const benefits = [
    { metric: '50%', label: 'Faster Sales', description: 'Professional photos sell properties 50% faster' },
    { metric: '118%', label: 'More Views', description: 'Quality images generate up to 118% more interest' },
    { metric: '60%', label: 'Photo Focus', description: 'Buyers spend 60% of their time viewing photos' },
    { metric: '83%', label: 'Critical Factor', description: 'Of buyers rank photos the most important element' },
  ];

  const whyEnhance = [
    { title: 'Sell Properties 50% Faster', description: 'Professional quality photos are statistically proven to sell listings 50% faster than amateur photography.', icon: '⚡' },
    { title: 'Increase Sale Price', description: 'Including professional quality photos has been proven to drastically increase the sale price of a property.', icon: '💰' },
    { title: 'Increase Prospects & Buyer Interest', description: 'Professional quality images have been shown to generate up to 118% more online views.', icon: '👥' },
    { title: 'Home Buyers Want Beautiful Photos', description: 'Home buyers spend 60% of their time viewing photos, while only 20% on the property description.', icon: '📸' },
  ];

  const beforeAfterExamples = [
    { before: '/images/gallery/3enbf.webp?auto=compress&cs=tinysrgb&w=600', after: '/images/gallery/3enaf.webp?auto=compress&cs=tinysrgb&w=600', title: 'Living Room Enhancement', turnaround: '4–12 Hours' },
    { before: '/images/gallery/2enbf.webp?auto=compress&cs=tinysrgb&w=600', after: '/images/gallery/2enaf.webp?auto=compress&cs=tinysrgb&w=600', title: 'Bedroom Transformation', turnaround: '4–12 Hours' },
    { before: '/images/gallery/enhancement-exterior-before.webp?auto=compress&cs=tinysrgb&w=600', after: '/images/gallery/enhancement-exterior-after.webp?auto=compress&cs=tinysrgb&w=600', title: 'Exterior Enhancement', turnaround: '4–12 Hours' },
  ];

  const dotGrid = `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`;

  return (
    <div className="bg-[#0B0D17] text-white">
      <Header />

      {/* ===================== HERO ===================== */}
      <section className="relative overflow-hidden bg-[#0B0D17] border-b border-white/[0.06]">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-[1000px] h-[1000px] bg-purple-600/10 rounded-full blur-[160px] animate-glow-breathe" />
          <div className="absolute bottom-1/4 right-1/4 w-[800px] h-[800px] bg-blue-600/5 rounded-full blur-[140px] animate-glow-breathe-delayed" />
          <div className="absolute inset-0 opacity-[0.025]" style={{ backgroundImage: dotGrid }} />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-purple-500 text-white px-5 py-2 rounded-full text-sm font-bold shadow-lg shadow-purple-500/30 animate-pulse">
                  🎉 50% OFF - LIMITED TIME!
                </div>
                <div className="inline-flex items-center bg-white/[0.04] border border-white/[0.12] text-purple-200 px-5 py-2 rounded-full text-sm font-bold">
                  STARTING AT $0.40 PER IMAGE
                </div>
              </div>

              <div className="space-y-6">
                <h1 className="text-5xl lg:text-7xl font-bold leading-[1.05] tracking-tight">
                  Image{' '}
                  <span className="bg-gradient-to-r from-purple-400 via-purple-300 to-blue-400 bg-clip-text text-transparent">
                    Enhancement
                  </span>
                </h1>
                <p className="text-xl text-gray-400 leading-relaxed">
                  Transform ordinary property photos into stunning, market-ready images with our comprehensive
                  17-step enhancement process.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link href={signupUrl}>
                  <button className="group w-full sm:w-auto px-9 py-4 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white rounded-xl font-bold text-lg transition-all duration-300 flex items-center justify-center gap-2 shadow-xl shadow-purple-500/30 hover:shadow-purple-500/50 hover:-translate-y-0.5">
                    Start Free — Pay After Editing
                    <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </button>
                </Link>
                <Link href="#process">
                  <button className="w-full sm:w-auto px-9 py-4 bg-white/[0.03] hover:bg-white/[0.08] backdrop-blur-sm border border-white/[0.15] hover:border-white/30 text-white rounded-xl font-bold text-lg transition-all duration-300">
                    See Our Process
                  </button>
                </Link>
              </div>

              <p className="text-sm text-gray-500">
                ✓ No credit card • ✓ No upfront payment • ✓ Pay only if satisfied
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-4">
                {benefits.map((benefit, i) => (
                  <div key={i} className="text-center bg-white/[0.03] border border-white/[0.08] rounded-xl p-4 hover:border-purple-500/30 transition-all duration-300">
                    <div className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent mb-1">{benefit.metric}</div>
                    <div className="text-xs font-semibold text-white mb-1">{benefit.label}</div>
                    <div className="text-xs text-gray-500 leading-tight">{benefit.description}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-4 bg-purple-600/15 rounded-3xl blur-[80px] -z-10" />
              <div className="relative h-[450px] lg:h-[550px] rounded-2xl overflow-hidden shadow-2xl border-2 border-white/[0.12]">
                <ProtectedAppImage
                  src="/images/gallery/1ddbf.webp?auto=compress&cs=tinysrgb&w=1200"
                  alt="Enhanced property photo"
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-6 right-6 bg-gradient-to-r from-purple-600 to-purple-500 text-white px-5 py-3 rounded-lg font-bold text-sm shadow-lg shadow-purple-500/30">
                  AFTER ENHANCEMENT
                </div>
                <div className="absolute bottom-6 left-6 bg-black/60 backdrop-blur-sm border border-white/10 text-white px-5 py-3 rounded-lg font-bold text-sm">
                  4–12 HOUR TURNAROUND
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== WHY ENHANCE ===================== */}
      <section className="relative py-20 lg:py-28 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight mb-4">
              Why enhance your listing photos?
            </h2>
            <p className="text-lg text-gray-400 leading-relaxed">
              Professional photo enhancement isn&apos;t just about making images look better — it&apos;s about driving
              real business results.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyEnhance.map((item, index) => (
              <div key={index} className="group text-center bg-[#13151F]/80 backdrop-blur-sm border border-white/[0.08] rounded-2xl p-8 hover:border-purple-500/30 hover:-translate-y-1 transition-all duration-300">
                <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-purple-500/20 to-blue-500/10 border border-purple-500/20 flex items-center justify-center text-3xl mb-5 group-hover:scale-110 transition-transform">
                  {item.icon}
                </div>
                <h3 className="text-lg font-bold text-white mb-3">{item.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== 17-STEP PROCESS ===================== */}
      <section id="process" className="relative py-20 lg:py-28 px-6 lg:px-12 bg-white/[0.015] border-y border-white/[0.06] scroll-mt-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-purple-500 text-white px-6 py-2.5 rounded-full text-sm font-bold mb-4 shadow-lg shadow-purple-500/30">
              🔥 50% OFF - WAS $0.80, NOW $0.40!
            </div>
            <div className="mb-6">
              <span className="inline-block bg-white/[0.04] border border-white/[0.12] text-purple-200 px-6 py-2 rounded-full text-sm font-bold">
                OUR $0.40, 17-STEP ENHANCEMENT PROCESS
              </span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight mb-4">
              Comprehensive enhancement process
            </h2>
            <p className="text-lg text-gray-400 leading-relaxed">
              Every image receives meticulous attention through our proven 17-step workflow, each designed to
              transform your photos into market-ready masterpieces.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
            {enhancements.map((enhancement, index) => (
              <div key={index} className="group bg-[#13151F]/80 backdrop-blur-sm border border-white/[0.08] rounded-2xl p-6 hover:border-purple-500/30 transition-all duration-300">
                <div className="flex items-start gap-4">
                  <div className="text-3xl flex-shrink-0 group-hover:scale-110 transition-transform duration-300">{enhancement.icon}</div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-bold text-purple-300 bg-purple-500/10 border border-purple-500/20 px-2 py-0.5 rounded">STEP {index + 1}</span>
                      <span className="text-xs font-bold text-green-300 bg-green-500/10 border border-green-500/20 px-2 py-0.5 rounded">INCLUDED</span>
                    </div>
                    <h3 className="text-base font-bold text-white mb-1.5">{enhancement.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{enhancement.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Optional Extra */}
          <div className="max-w-3xl mx-auto">
            <div className="p-8 rounded-2xl bg-gradient-to-r from-purple-500/[0.08] to-blue-500/[0.05] border border-purple-500/20">
              <div className="flex items-start gap-6">
                <div className="text-5xl">{optionalExtra.icon}</div>
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-3 mb-3">
                    <h3 className="text-2xl font-bold text-white">{optionalExtra.title}</h3>
                    <span className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">{optionalExtra.price}</span>
                    <span className="text-xs font-bold text-purple-200 bg-white/[0.06] border border-white/10 px-3 py-1 rounded-full">OPTIONAL EXTRA</span>
                  </div>
                  <p className="text-gray-400 leading-relaxed">{optionalExtra.description}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== BEFORE / AFTER ===================== */}
      <section className="relative py-20 lg:py-28 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 max-w-2xl mx-auto">
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight mb-4">See the transformation</h2>
            <p className="text-lg text-gray-400 leading-relaxed">
              Real before and after examples showcasing the power of professional image enhancement.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {beforeAfterExamples.map((example, index) => (
              <div key={index} className="bg-[#13151F]/80 backdrop-blur-sm rounded-2xl overflow-hidden border border-white/[0.08] hover:border-purple-500/30 transition-all duration-300">
                <div className="grid grid-cols-2 gap-0">
                  <div className="relative h-48">
                    <ProtectedAppImage src={example.before} alt={`${example.title} - Before`} className="w-full h-full object-cover" />
                    <div className="absolute top-3 left-3 bg-black/60 backdrop-blur-sm border border-white/10 text-white px-3 py-1 text-xs font-bold rounded">BEFORE</div>
                  </div>
                  <div className="relative h-48">
                    <ProtectedAppImage src={example.after} alt={`${example.title} - After`} className="w-full h-full object-cover" />
                    <div className="absolute top-3 right-3 bg-gradient-to-r from-purple-600 to-purple-500 text-white px-3 py-1 text-xs font-bold rounded shadow-lg shadow-purple-500/30">AFTER</div>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-bold text-lg text-white mb-2">{example.title}</h3>
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{example.turnaround}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== GET STARTED / ZERO RISK ===================== */}
      <section className="relative py-20 lg:py-28 px-6 lg:px-12 bg-white/[0.015] border-y border-white/[0.06]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight mb-8">Ready to get started?</h2>

          <div className="relative bg-[#13151F]/95 backdrop-blur-2xl border-2 border-white/[0.12] rounded-2xl p-8 mb-8 shadow-[0_30px_80px_rgba(0,0,0,0.5)]">
            <div className="absolute -inset-4 bg-purple-600/10 rounded-3xl blur-[80px] -z-10" />
            <div className="flex items-center justify-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500/20 to-emerald-500/10 border border-green-500/30 flex items-center justify-center text-green-400">
                <Check className="w-7 h-7" />
              </div>
              <h3 className="text-2xl lg:text-3xl font-bold text-white">Pay ONLY after you&apos;re satisfied</h3>
            </div>
            <p className="text-lg text-gray-400 mb-6">
              Upload your images → We edit them professionally → You review the results → Pay only if you love them
            </p>
            <div className="grid md:grid-cols-3 gap-4 text-sm">
              {['No Upfront Payment', 'No Credit Card', '100% Risk-Free'].map((t) => (
                <div key={t} className="flex items-center justify-center gap-2 text-green-400 font-semibold">
                  <Check /> {t}
                </div>
              ))}
            </div>
          </div>

          <p className="text-lg text-gray-400 mb-8">
            Join thousands of satisfied real estate professionals. Start with zero risk today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href={signupUrl}>
              <button className="px-9 py-4 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white rounded-xl font-bold text-lg transition-all duration-300 shadow-xl shadow-purple-500/30 hover:-translate-y-0.5">
                Start Free — Pay After Editing
              </button>
            </Link>
            <Link href="/auth/signin">
              <button className="px-9 py-4 bg-white/[0.03] hover:bg-white/[0.08] backdrop-blur-sm border border-white/[0.15] hover:border-white/30 text-white rounded-xl font-bold text-lg transition-all duration-300">
                Sign In
              </button>
            </Link>
          </div>
          <p className="text-sm text-gray-500 mt-6">✓ No credit card required • ✓ Review before paying • ✓ Cancel anytime</p>
        </div>
      </section>

      {/* ===================== PRICING ===================== */}
      <section className="relative py-20 lg:py-28 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 max-w-2xl mx-auto">
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight mb-4">Transparent, affordable pricing</h2>
            <p className="text-lg text-gray-400 leading-relaxed">
              Professional enhancement at unbeatable prices. No hidden fees, no subscriptions, no surprises.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto items-start">
            {/* Standard */}
            <div className="bg-[#13151F]/80 backdrop-blur-sm border border-white/[0.08] rounded-2xl p-8 hover:border-purple-500/30 transition-all duration-300">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-white mb-3">Standard</h3>
                <div className="flex items-baseline justify-center gap-2">
                  <span className="text-5xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">$0.40</span>
                  <span className="text-gray-500">/image</span>
                </div>
                <div className="mt-2">
                  <span className="text-gray-500 line-through text-sm">Was $0.80</span>
                  <span className="ml-2 text-purple-300 font-bold text-sm">Save 50%!</span>
                </div>
                <p className="text-gray-500 text-sm mt-3">Perfect for individual listings</p>
              </div>
              <ul className="space-y-3 mb-8">
                {['All 17 enhancement steps', '4–12 hour turnaround', 'Unlimited revisions', 'Professional quality'].map((f) => (
                  <li key={f} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-400">{f}</span>
                  </li>
                ))}
              </ul>
              <Link href={signupUrl}>
                <button className="w-full py-3.5 bg-white/[0.05] border border-white/[0.12] text-white rounded-xl font-bold hover:bg-white/[0.1] transition-all">Get Started</button>
              </Link>
            </div>

            {/* HDR - popular */}
            <div className="relative bg-gradient-to-b from-purple-600/20 to-[#13151F]/90 backdrop-blur-sm border-2 border-purple-500/40 rounded-2xl p-8 shadow-[0_30px_80px_rgba(124,58,237,0.25)] md:scale-105">
              <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-gradient-to-r from-purple-500 to-blue-500 text-white px-5 py-1.5 rounded-full text-xs font-bold shadow-lg">
                MOST POPULAR
              </div>
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-white mb-3">HDR Enhancement</h3>
                <div className="flex items-baseline justify-center gap-2">
                  <span className="text-5xl font-bold text-white">$0.80</span>
                  <span className="text-gray-300">/image</span>
                </div>
                <div className="mt-2">
                  <span className="text-gray-400 line-through text-sm">Was $1.60</span>
                  <span className="ml-2 text-purple-200 font-bold text-sm">Save 50%!</span>
                </div>
                <p className="text-gray-300 text-sm mt-3">With HDR bracketing - Best value</p>
              </div>
              <ul className="space-y-3 mb-8">
                {['Everything in Standard', 'Priority processing', 'Dedicated editor', 'Volume discounts'].map((f) => (
                  <li key={f} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-purple-300 flex-shrink-0 mt-0.5" />
                    <span className="text-white">{f}</span>
                  </li>
                ))}
              </ul>
              <Link href={signupUrl}>
                <button className="w-full py-3.5 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white rounded-xl font-bold transition-all shadow-lg shadow-purple-500/30 hover:-translate-y-0.5">Start Project</button>
              </Link>
            </div>

            {/* Enterprise */}
            <div className="bg-[#13151F]/80 backdrop-blur-sm border border-white/[0.08] rounded-2xl p-8 hover:border-purple-500/30 transition-all duration-300">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-white mb-3">Enterprise</h3>
                <div className="flex items-baseline justify-center gap-2">
                  <span className="text-5xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">Custom</span>
                </div>
                <p className="text-gray-500 text-sm mt-3">For agencies &amp; large teams</p>
              </div>
              <ul className="space-y-3 mb-8">
                {['Custom pricing plans', 'API integration', 'Dedicated account manager', 'White-label options'].map((f) => (
                  <li key={f} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-400">{f}</span>
                  </li>
                ))}
              </ul>
              <Link href="/contact">
                <button className="w-full py-3.5 bg-white/[0.05] border border-white/[0.12] text-white rounded-xl font-bold hover:bg-white/[0.1] transition-all">Contact Sales</button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== QUALITY GUARANTEE ===================== */}
      <section className="relative py-20 lg:py-28 px-6 lg:px-12 bg-white/[0.015] border-y border-white/[0.06]">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight">
              Quality{' '}
              <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">guaranteed.</span>
            </h2>
            <p className="text-lg text-gray-400 leading-relaxed">
              We stand behind our work with a 100% satisfaction guarantee. If you&apos;re not happy, we&apos;ll make it
              right or refund your money.
            </p>
            <ul className="space-y-4">
              {[
                '4–12 hour turnaround time',
                'Unlimited free revisions',
                'Expert editors with 10+ years experience',
                'ISO 9001 certified quality control',
                '100% satisfaction guarantee',
                'Secure file handling & privacy',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-purple-400 flex-shrink-0" />
                  <span className="text-gray-300 text-lg">{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 bg-purple-600/15 rounded-3xl blur-[80px] -z-10" />
            <div className="relative h-[500px] rounded-2xl overflow-hidden shadow-2xl border-2 border-white/[0.12]">
              <ProtectedAppImage
                src="/images/gallery/day-dusk-residential-before.webp?auto=compress&cs=tinysrgb&w=1200"
                alt="Quality guarantee"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ===================== FINAL CTA ===================== */}
      <section className="relative py-20 lg:py-28 px-6 lg:px-12 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] bg-purple-600/10 rounded-full blur-[160px] animate-glow-breathe" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <div className="relative bg-[#13151F]/95 backdrop-blur-2xl rounded-3xl border-2 border-white/[0.12] shadow-[0_30px_80px_rgba(0,0,0,0.6)] p-12 lg:p-16">
            <div className="absolute -inset-4 bg-purple-600/15 rounded-3xl blur-[80px] -z-10" />
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight mb-5">
              Ready to transform your{' '}
              <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">photos?</span>
            </h2>
            <p className="text-lg text-gray-400 leading-relaxed mb-10 max-w-2xl mx-auto">
              Join thousands of real estate professionals who trust Shashio for professional image enhancement.
              Get started today and see the difference quality photos make.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href={signupUrl}>
                <button className="px-10 py-4 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white font-bold text-lg rounded-xl transition-all duration-300 shadow-xl shadow-purple-500/30 hover:-translate-y-0.5">
                  Start Your Project
                </button>
              </Link>
              <Link href="/homepage#pricing">
                <button className="px-10 py-4 bg-white/[0.03] hover:bg-white/[0.08] backdrop-blur-sm text-white font-bold text-lg rounded-xl transition-all duration-300 border border-white/[0.15] hover:border-white/30">
                  View Pricing
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* Animations */}
      <style jsx global>{`
        @keyframes glow-breathe {
          0%, 100% { opacity: 0.1; transform: scale(1); }
          50% { opacity: 0.15; transform: scale(1.05); }
        }
        @keyframes glow-breathe-delayed {
          0%, 100% { opacity: 0.05; transform: scale(1); }
          50% { opacity: 0.08; transform: scale(1.03); }
        }
        .animate-glow-breathe { animation: glow-breathe 8s ease-in-out infinite; }
        .animate-glow-breathe-delayed { animation: glow-breathe-delayed 10s ease-in-out infinite; animation-delay: 2s; }
      `}</style>
    </div>
  );
}
